'use client';

import { type ReactNode } from 'react';
import {
  Info,
  Coins,
  Users,
  LayoutGrid,
  Activity,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMobileChrome } from '@/contexts/mobile-chrome-context';

/**
 * Identifiers for the mobile panel chip rail.
 *
 * `board` is the home chip — selected by default, it returns the viewer
 * to the canvas. Any other chip opens the matching side panel as a
 * `MobilePanelOverlay` above the canvas.
 *
 * `mytiles` was removed: the viewer's owned seats now render as a
 * permanent horizontal pill strip below the chip rail, so the chip
 * wasn't earning its footprint and the overlay treatment duplicated
 * the strip's content.
 *
 * `chat` was removed from the rail in production — chat moved to the
 * bottom-nav Chat tab and lives on its own `/chat` route (board list
 * + per-room view) so it reads as its own server list rather than a
 * board-local panel. The id stays in the union because the tutorial
 * still needs to address the chat overlay programmatically (it sets
 * `mobilePanel = 'chat'` to mount the read-only `TutorialChatBody` for
 * the `holder-chat` beat); see `LobbyClient`'s tutorial chat overlay
 * branch.
 */
export type MobilePanelId =
  | 'board'
  | 'chat'
  | 'info'
  | 'rewards'
  | 'holders'
  | 'yield';

export interface MobilePanelChipDef {
  id: MobilePanelId;
  label: string;
  icon: ReactNode;
  /** Optional unread dot rendered on the chip. Currently unused now
   *  that chat lives off the rail; kept for future per-chip signal
   *  surfaces (e.g. an "Activity" pulse during a streak). */
  badge?: 'dot' | null;
}

const DEFAULT_CHIPS: MobilePanelChipDef[] = [
  { id: 'board', label: 'Board', icon: <LayoutGrid className="h-3.5 w-3.5" /> },
  { id: 'info', label: 'Info', icon: <Info className="h-3.5 w-3.5" /> },
  { id: 'rewards', label: 'Activity', icon: <Activity className="h-3.5 w-3.5" /> },
  { id: 'holders', label: 'Holders', icon: <Users className="h-3.5 w-3.5" /> },
  { id: 'yield', label: 'Yield', icon: <Coins className="h-3.5 w-3.5" /> },
];

interface MobilePanelChipRailProps {
  /**
   * Currently-active panel. `'board'` means the canvas is showing; any
   * other id surfaces that panel in the `MobilePanelOverlay`.
   */
  active: MobilePanelId;
  /**
   * Change callback. Tapping the already-active chip is a no-op at this
   * layer — `'board'` is the only way to dismiss a panel back to the
   * canvas, so parents don't need to reason about `null`.
   */
  onChange: (panel: MobilePanelId) => void;
  /** Optional per-chip badges keyed by panel id. */
  badges?: Partial<Record<MobilePanelId, 'dot' | null>>;
  className?: string;
}

/**
 * Horizontal chip rail rendered above the board on mobile. Tapping a chip
 * either opens the matching `MobilePanelOverlay` (filling the space above
 * the board rail) or dismisses it back to the canvas.
 */
export function MobilePanelChipRail({
  active,
  onChange,
  badges,
  className,
}: MobilePanelChipRailProps) {
  return (
    <div
      className={cn(
        'w-full overflow-x-auto snap-x snap-mandatory scrollbar-none',
        'bg-gradient-to-b from-black/85 to-black/60 backdrop-blur-sm',
        'border-y border-amber-500/20',
        className,
      )}
      role="tablist"
      aria-label="Board panels"
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        {DEFAULT_CHIPS.map((chip) => {
          const isActive = active === chip.id;
          const badge = badges?.[chip.id];
          return (
            <button
              key={chip.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => {
                if (isActive) return;
                onChange(chip.id);
              }}
              // `${chip.id}-pill` is the tutorial highlight target for
              // the panel-chip steps (`yield-panel`, `rewards-stream`,
              // `holder-chat`). The bare `chip.id` collides with
              // `MobilePanelOverlay`'s own `data-tutorial-target` (the
              // overlay panel uses the same id), so the pill suffix
              // disambiguates: the script asks for `'yield-pill'` and
              // the resolver lands on the chip rather than the open
              // panel underneath. See `tutorial-script.ts`.
              data-tutorial-target={`${chip.id}-pill`}
              className={cn(
                'relative snap-start shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors',
                isActive
                  ? 'bg-amber-500/25 border-amber-400/60 text-amber-100 shadow-[0_0_0_1px_rgba(255,215,120,0.25)]'
                  : 'bg-black/40 border-amber-500/20 text-zinc-300 hover:text-amber-100 hover:border-amber-400/40',
              )}
            >
              <span className="shrink-0">{chip.icon}</span>
              <span>{chip.label}</span>
              {badge === 'dot' && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-brand-red" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Shell-mounted host for {@link MobilePanelChipRail}. Reads the active
 * chip-rail config from {@link useMobileChrome} (registered by
 * `LobbyClient` / `/chat` via `useChipRail`) and renders the rail when
 * a consumer is registered. Hidden when no consumer registered (e.g.
 * on `/create`, `?mode=lobby`, or routes outside the mobile-chrome
 * match list).
 *
 * The host stays in `AppShell.MobileChromeTop` so the rail is part of
 * the fixed top stack — the canvas / overlays beneath have a stable
 * `100dvh - <fixed-top> - <fixed-bottom>` slot to render into. The
 * old in-`LobbyClient` mount had to be measured by ResizeObserver and
 * subtracted from the canvas height; hoisting to the shell collapses
 * that math to a CSS variable on the document root.
 */
export function MobileChipRailHost({ className }: { className?: string }) {
  const ctx = useMobileChrome();
  if (!ctx || ctx.active === null) return null;
  return (
    <MobilePanelChipRail
      active={ctx.active}
      onChange={ctx.triggerChange}
      badges={ctx.badges}
      className={className}
    />
  );
}

interface MobilePanelOverlayProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Hide the default padding on the body so full-bleed panels like
   *  ChatPanel (variant=docked) can use the whole space themselves. */
  noBodyPadding?: boolean;
  /** Optional `data-tutorial-target` marker. The tutorial highlight
   *  resolver looks up `data-tutorial-target` BEFORE `data-hud-id`,
   *  so tagging the visible mobile overlay with the panel's id (e.g.
   *  `'yield'` / `'rewards'` / `'chat'`) lands the spotlight rect on
   *  the panel actually on screen — without this, the lookup would
   *  hit the off-screen desktop floating panel that shares the
   *  `data-hud-id` and produce a `{0,0,0,0}` bounding rect. */
  dataTutorialTarget?: string;
}

/**
 * Full-bleed overlay that fills the canvas region when a panel chip is
 * selected on mobile. The board canvas keeps rendering underneath so
 * dismissal is instant. `MobilePanelOverlay` intentionally does *not*
 * trap focus — users dismiss via the close button or by tapping the chip
 * again.
 */
export function MobilePanelOverlay({
  title,
  onClose,
  children,
  className,
  noBodyPadding,
  dataTutorialTarget,
}: MobilePanelOverlayProps) {
  return (
    <div
      className={cn(
        'absolute inset-0 z-30 flex flex-col bg-[#050404]/97 backdrop-blur-md',
        'border-t border-amber-500/25 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.08)]',
        className,
      )}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      data-tutorial-target={dataTutorialTarget}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-amber-500/20">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-100">
          {title}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${title}`}
          className="inline-flex items-center justify-center h-7 w-7 rounded-[3px] bg-black/40 border border-amber-500/20 text-zinc-200 hover:text-amber-100 hover:border-amber-400/50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* The body is the only scroll surface inside the overlay. We
          enforce `flex-1 min-h-0` so the flex parent can size it
          correctly under iOS Safari (which sometimes defaults flex
          children to `min-height: auto` and leaks scroll into the
          page). `pb-12` keeps the last row from sitting flush with
          the visible bottom — without that breathing room a single
          tall card (e.g. AdapterStack on a wallet with several
          adapters) renders flush against the closing border and the
          user can't tell whether they've scrolled to the end. */}
      <div
        className={cn(
          'flex-1 min-h-0 overflow-y-auto overscroll-contain',
          !noBodyPadding && 'p-3 pb-12',
        )}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </div>
    </div>
  );
}
